import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import classNames from "classnames";
import {
  sideBarSelectedAtom,
  sideBarWidthAtom,
  SUMMARY,
  sidebarAnimationAtom,
  isBlankCanvasAtom,
  isDraftModeIncludingTemplatesAtom,
  nodesAtom,
} from "@zapier/canvas-state";
import {
  DEFAULT_SIDEBAR_WIDTH,
  PRODUCT_NAV_WIDTH,
} from "@zapier/canvas-constants";
import { CopilotChatPanel } from "@zapier/ai-toolkit/client";
import { IconButton } from "@zapier/design-system";
import { getCopilotSuggestions } from "@zapier/copilot-tools";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";
import { useCopilotDrawerState } from "../../hooks/useCopilotDrawerState";
import { SummaryPanel } from "../SummaryPanel";
import DevTools from "../DevTools/DevTools";
import styles from "./SidebarDrawer.module.css";

type SidebarDrawerProps = {
  onCloseClick: () => void;
};

export default function SidebarDrawer({ onCloseClick }: SidebarDrawerProps) {
  const sideBarSelected = useAtomValue(sideBarSelectedAtom);
  const [isCopilotOpen] = useCopilotDrawerState();
  const sidebarAnimation = useAtomValue(sidebarAnimationAtom);
  const setSideBarWidth = useSetAtom(sideBarWidthAtom);

  // Atoms needed for copilot suggestions
  const isBlankCanvas = useAtomValue(isBlankCanvasAtom);
  const isDraftModeIncludingTemplates = useAtomValue(
    isDraftModeIncludingTemplatesAtom,
  );
  const nodes = useAtomValue(nodesAtom);

  // Generate suggestions based on canvas state
  const copilotSuggestions = getCopilotSuggestions({
    isBlankCanvas,
    isDraftModeIncludingTemplates,
    nodes,
  });

  useEffect(() => {
    if (sideBarSelected || isCopilotOpen) {
      setSideBarWidth(PRODUCT_NAV_WIDTH + DEFAULT_SIDEBAR_WIDTH);
    } else {
      setSideBarWidth(PRODUCT_NAV_WIDTH);
    }
  }, [sideBarSelected, setSideBarWidth, isCopilotOpen]);

  const SIDEBAR_CONTENT = {
    [SUMMARY]: {
      title: capitalizeFirstLetter(SUMMARY),
      component: (
        <>
          <div className={styles.titleContainer}>
            <div>{capitalizeFirstLetter(SUMMARY)}</div>
            <IconButton
              name="arrowSmallLeft"
              variant="tertiary"
              size="small"
              onPress={onCloseClick}
            />
          </div>
          <div className={styles.componentContainer}>
            <SummaryPanel />
          </div>
        </>
      ),
    },
    devTools: {
      title: "🔒 DevTools - staff only (⌘D)",
      component: (
        <>
          <div className={styles.titleContainer}>
            <div>🔒 DevTools - staff only (⌘D)</div>{" "}
            <IconButton
              name="arrowSmallLeft"
              variant="tertiary"
              size="small"
              onPress={onCloseClick}
            />
          </div>
          <div className={styles.componentContainer}>
            <DevTools />
          </div>
        </>
      ),
    },
    copilot: {
      title: "Copilot",
      component: (
        <div
          className={classNames(styles.copilotChatPanelContainer, {
            [styles.fadeIn]: sidebarAnimation === "fadeIn",
          })}
        >
          <CopilotChatPanel
            placeholder="Ask me anything about your canvas..."
            onCloseClick={onCloseClick}
            emptyStateSuggestions={copilotSuggestions}
          />
        </div>
      ),
    },
  };

  const selectedSidebar = (() => {
    // If copilot is open, show copilot sidebar
    if (isCopilotOpen) {
      return SIDEBAR_CONTENT.copilot;
    }

    // Otherwise, show the selected sidebar if it exists
    if (
      sideBarSelected &&
      SIDEBAR_CONTENT[sideBarSelected as keyof typeof SIDEBAR_CONTENT]
    ) {
      return SIDEBAR_CONTENT[sideBarSelected as keyof typeof SIDEBAR_CONTENT];
    }

    // Default fallback
    return {
      title: "",
      component: null,
    };
  })();

  return (
    <div className={styles.drawerContainer}>
      <div style={{ minWidth: DEFAULT_SIDEBAR_WIDTH, height: "100%" }}>
        {selectedSidebar.component}
      </div>
    </div>
  );
}
